/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { ShimmerText } from '@atlassian/rovo-platform-ui-components/primitives/ShimmerText';

import messages from './messages';

const styles = cssMap({
	container: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		backgroundColor: token('color.background.input'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		// @ts-expect-error - raw boxShadow value has no design token equivalent — custom shadow required by design spec
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-unsafe-values
		boxShadow: '0 -2px 50px 8px rgba(30, 31, 33, 0.08)',
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
	},
	inputArea: {
		flex: '1 0 0',
		minWidth: '0px',
		paddingInline: token('space.075'),
	},
	sendArea: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
	},
});

export type AwaitingUserResponseViewProps = {
	conversationId: string;
	actionInvocationId: string;
	onStopTask?: () => void;
};

export const AwaitingUserResponseView = ({
	conversationId,
	actionInvocationId,
	onStopTask,
}: AwaitingUserResponseViewProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate-actions');
	const { sendAnalyticsEvent } = useAnalytics();
	const [isHovered, setIsHovered] = useState(false);

	const handleSkipAll = () => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'taskQuestionCardSkipAll',
			attributes: {
				source: 'taskStatusBar',
				conversationId,
				invocationId: actionInvocationId,
			},
		});
		publish({
			type: 'task-skip-all-questions',
			source: 'chatInputOverlay',
			data: { conversationId, invocationId: actionInvocationId },
		});
	};

	return (
		<Box xcss={styles.container} testId="awaiting-user-response-view">
			<Box xcss={styles.inputArea}>
				<ShimmerText>{formatMessage(messages.awaitingUserResponseTitle)}</ShimmerText>
			</Box>
			<Inline alignBlock="center" space="space.050">
				<Button appearance="subtle" onClick={handleSkipAll}>
					{formatMessage(messages.skipAllButton)}
				</Button>
				<div
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					onFocus={() => setIsHovered(true)}
					onBlur={() => setIsHovered(false)}
				>
					{isHovered ? (
						<IconButton
							appearance="subtle"
							onClick={onStopTask}
							icon={(iconProps) => (
								<VideoStopOverlayIcon {...iconProps} color={token('color.icon.danger')} label="" />
							)}
							label={formatMessage(messages.stopTaskLabel)}
						/>
					) : (
						<Box xcss={styles.sendArea}>
							<Spinner size={16} label={formatMessage(messages.sendButtonSpinnerLabel)} />
						</Box>
					)}
				</div>
			</Inline>
		</Box>
	);
};
